import React, { useState, useEffect, useRef } from 'react';
import { searchFoods, getFoodByName, REACTIONS } from '../utils/foods';

function EventForm({ type, event, onSave, onCancel }) {
  const [datetime, setDatetime] = useState(() => {
    const now = new Date();
    const offset = now.getTimezoneOffset() * 60000;
    return new Date(now.getTime() - offset).toISOString().slice(0, 16);
  });
  const [loading, setLoading] = useState(false);

  // États spécifiques par type d'événement
  const [feedingMode, setFeedingMode] = useState('biberon');
  const [breastSide, setBreastSide] = useState('gauche');
  const [amount, setAmount] = useState('120');
  const [milkType, setMilkType] = useState('lait_infantile');

  const [hasUrine, setHasUrine] = useState(true);
  const [hasStool, setHasStool] = useState(false);
  const [stoolType, setStoolType] = useState('normal');

  const [temperature, setTemperature] = useState('37.0');

  // États pour la diversification (plusieurs aliments possibles)
  const [foodSearch, setFoodSearch] = useState('');
  const [selectedFoods, setSelectedFoods] = useState([]); // Tableau d'objets {name, emoji, reaction}
  const [foodSuggestions, setFoodSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const suggestionsRef = useRef(null);

  // Remplir les champs si on édite un événement existant
  useEffect(() => {
    if (event) {
      // Convertir la date pour l'input datetime-local
      const eventDate = new Date(event.created_at);
      const offset = eventDate.getTimezoneOffset() * 60000;
      setDatetime(new Date(eventDate.getTime() - offset).toISOString().slice(0, 16));

      // Parser les données de l'événement
      const data = JSON.parse(event.data);

      switch (event.type) {
        case 'feeding':
          if (data.mode === 'biberon') {
            setFeedingMode('biberon');
            setAmount(data.amount_ml.toString());
            setMilkType(data.milk_type);
          } else {
            setFeedingMode('sein');
            setBreastSide(data.mode.replace('sein_', ''));
          }
          // Charger les données de diversification si présentes
          if (data.foods && Array.isArray(data.foods)) {
            // Nouveau format multi-aliments
            setSelectedFoods(data.foods.map(f => ({
              name: f.name,
              emoji: f.emoji || getFoodByName(f.name)?.emoji || '🥄',
              reaction: f.reaction || ''
            })));
          } else if (data.food_name) {
            // Ancien format mono-aliment (rétrocompatibilité)
            const food = getFoodByName(data.food_name);
            setSelectedFoods([{
              name: data.food_name,
              emoji: food?.emoji || data.food_emoji || '🥄',
              reaction: data.reaction || ''
            }]);
          }
          break;
        case 'diaper':
          setHasUrine(data.urine);
          setHasStool(data.stool);
          if (data.stool) {
            setStoolType(data.stool_type);
          }
          break;
        case 'temperature':
          setTemperature(data.value.toString());
          break;
        case 'bath':
          // Pas de données spécifiques pour le bain
          break;
        default:
          break;
      }
    }
  }, [event]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    let eventData = {};

    switch (type) {
      case 'feeding':
        if (feedingMode === 'biberon') {
          eventData = {
            mode: 'biberon',
            amount_ml: parseInt(amount),
            milk_type: milkType
          };
        } else {
          eventData = {
            mode: `sein_${breastSide}`
          };
        }
        // Ajouter les données de diversification si des aliments sont sélectionnés
        if (selectedFoods.length > 0) {
          eventData.foods = selectedFoods.map(f => ({
            name: f.name,
            emoji: f.emoji,
            reaction: f.reaction || null
          }));
        }
        break;

      case 'diaper':
        eventData = {
          urine: hasUrine,
          stool: hasStool,
          stool_type: hasStool ? stoolType : null
        };
        break;

      case 'temperature':
        eventData = {
          value: parseFloat(temperature)
        };
        break;

      case 'bath':
        eventData = {};
        break;

      default:
        break;
    }

    try {
      const token = localStorage.getItem('token');
      const isEdit = !!event;
      const url = isEdit ? `/api/events/${event.id}` : '/api/events';
      const method = isEdit ? 'PUT' : 'POST';

      const requestBody = isEdit
        ? { data: eventData, created_at: new Date(datetime).toISOString() }
        : { type, data: eventData, created_at: new Date(datetime).toISOString() };

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(requestBody)
      });

      const result = await response.json();
      if (result.success) {
        onSave();
      } else {
        alert('Erreur lors de l\'enregistrement');
      }
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  const getTitle = () => {
    const isEdit = !!event;
    const titles = {
      feeding: isEdit ? '🍼 Modifier le repas' : '🍼 Nouveau repas',
      diaper: isEdit ? '👶 Modifier la couche' : '👶 Nouvelle couche',
      temperature: isEdit ? '🌡️ Modifier la température' : '🌡️ Nouvelle température',
      bath: isEdit ? '💧 Modifier le bain' : '💧 Nouveau bain'
    };
    return titles[type] || (isEdit ? 'Modifier l\'événement' : 'Nouvel événement');
  };

  // Gestion de la recherche d'aliments
  const handleFoodSearchChange = (value) => {
    setFoodSearch(value);
    if (value.length >= 1) {
      // Filtrer les aliments déjà sélectionnés
      const selectedNames = selectedFoods.map(f => f.name.toLowerCase());
      const suggestions = searchFoods(value).filter(
        food => !selectedNames.includes(food.name.toLowerCase())
      );
      setFoodSuggestions(suggestions);
      setShowSuggestions(suggestions.length > 0);
    } else {
      setFoodSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleSelectFood = (food) => {
    // Ajouter l'aliment à la liste
    setSelectedFoods([...selectedFoods, { name: food.name, emoji: food.emoji, reaction: '' }]);
    setFoodSearch('');
    setShowSuggestions(false);
    setFoodSuggestions([]);
  };

  const handleRemoveFood = (index) => {
    setSelectedFoods(selectedFoods.filter((_, i) => i !== index));
  };

  const handleFoodReactionChange = (index, reaction) => {
    const updated = [...selectedFoods];
    updated[index].reaction = updated[index].reaction === reaction ? '' : reaction;
    setSelectedFoods(updated);
  };

  // Fermer les suggestions si on clique ailleurs
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, []);

  return (
    <div className="p-4 pb-20 max-h-screen overflow-y-auto">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-bold text-gray-900">{getTitle()}</h1>
        <button
          onClick={onCancel}
          className="text-gray-500 hover:text-gray-700"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Heure */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Heure
          </label>
          <input
            type="datetime-local"
            value={datetime}
            onChange={(e) => setDatetime(e.target.value)}
            className="input-field"
            required
          />
        </div>

        {/* Champs spécifiques selon le type */}
        {type === 'feeding' && (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Mode
              </label>
              <div className="space-y-2">
                <label className="flex items-center">
                  <input
                    type="radio"
                    value="biberon"
                    checked={feedingMode === 'biberon'}
                    onChange={(e) => setFeedingMode(e.target.value)}
                    className="mr-2"
                  />
                  Biberon
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    value="allaitement"
                    checked={feedingMode === 'allaitement'}
                    onChange={(e) => setFeedingMode(e.target.value)}
                    className="mr-2"
                  />
                  Allaitement
                </label>
              </div>
            </div>

            {feedingMode === 'biberon' ? (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Quantité (ml)
                  </label>
                  <input
                    type="number"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    className="input-field"
                    min="10"
                    max="300"
                    step="10"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Type de lait
                  </label>
                  <select
                    value={milkType}
                    onChange={(e) => setMilkType(e.target.value)}
                    className="input-field"
                  >
                    <option value="lait_infantile">Lait infantile</option>
                    <option value="lait_maternel">Lait maternel</option>
                  </select>
                </div>
              </>
            ) : (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Sein
                </label>
                <div className="space-y-2">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="gauche"
                      checked={breastSide === 'gauche'}
                      onChange={(e) => setBreastSide(e.target.value)}
                      className="mr-2"
                    />
                    Sein gauche
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="droit"
                      checked={breastSide === 'droit'}
                      onChange={(e) => setBreastSide(e.target.value)}
                      className="mr-2"
                    />
                    Sein droit
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="les_deux"
                      checked={breastSide === 'les_deux'}
                      onChange={(e) => setBreastSide(e.target.value)}
                      className="mr-2"
                    />
                    Les deux
                  </label>
                </div>
              </div>
            )}

            {/* Section Diversification (facultative) */}
            <div className="border-t border-gray-200 pt-4 mt-4">
              <div className="relative" ref={suggestionsRef}>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  🥕 Diversification <span className="text-gray-400 font-normal">(facultatif)</span>
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={foodSearch}
                    onChange={(e) => handleFoodSearchChange(e.target.value)}
                    onFocus={() => foodSuggestions.length > 0 && setShowSuggestions(true)}
                    placeholder="Rechercher un aliment..."
                    className="input-field pl-10"
                    autoComplete="off"
                  />
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xl">
                    🔍
                  </span>
                </div>

                {/* Liste de suggestions */}
                {showSuggestions && foodSuggestions.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    {foodSuggestions.map((food, index) => (
                      <button
                        key={index}
                        type="button"
                        onClick={() => handleSelectFood(food)}
                        className="w-full px-4 py-3 text-left hover:bg-primary-50 flex items-center space-x-3 border-b border-gray-100 last:border-b-0"
                      >
                        <span className="text-xl">{food.emoji}</span>
                        <span className="text-gray-900">{food.name}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Liste des aliments sélectionnés */}
              {selectedFoods.length > 0 && (
                <div className="mt-3 space-y-3">
                  {selectedFoods.map((food, index) => (
                    <div key={index} className="bg-orange-50 rounded-lg border border-orange-200 p-3">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center space-x-2">
                          <span className="text-xl">{food.emoji}</span>
                          <span className="text-orange-800 font-medium">{food.name}</span>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleRemoveFood(index)}
                          className="text-orange-600 hover:text-orange-800"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                      {/* Réaction pour cet aliment */}
                      <div className="flex flex-wrap gap-1">
                        {REACTIONS.map((r) => (
                          <button
                            key={r.value}
                            type="button"
                            onClick={() => handleFoodReactionChange(index, r.value)}
                            className={`px-2 py-1 rounded-full text-xs flex items-center space-x-1 transition-colors ${
                              food.reaction === r.value
                                ? r.value === 'allergy'
                                  ? 'bg-red-500 text-white'
                                  : 'bg-orange-500 text-white'
                                : 'bg-white border border-gray-300 text-gray-600 hover:border-orange-300'
                            }`}
                          >
                            <span>{r.emoji}</span>
                            <span>{r.label}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        {type === 'diaper' && (
          <>
            <div>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={hasUrine}
                  onChange={(e) => setHasUrine(e.target.checked)}
                  className="mr-2"
                />
                Urine
              </label>
            </div>
            <div>
              <label className="flex items-center mb-2">
                <input
                  type="checkbox"
                  checked={hasStool}
                  onChange={(e) => setHasStool(e.target.checked)}
                  className="mr-2"
                />
                Selles
              </label>
              {hasStool && (
                <select
                  value={stoolType}
                  onChange={(e) => setStoolType(e.target.value)}
                  className="input-field ml-6"
                >
                  <option value="normal">Normal</option>
                  <option value="liquide">Liquide</option>
                  <option value="dur">Dur</option>
                  <option value="couleur_inhabituelle">Couleur inhabituelle</option>
                </select>
              )}
            </div>
          </>
        )}

        {type === 'temperature' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Température (°C)
            </label>
            <input
              type="number"
              value={temperature}
              onChange={(e) => setTemperature(e.target.value)}
              className="input-field"
              min="35"
              max="41"
              step="0.1"
              required
            />
          </div>
        )}

        {type === 'bath' && (
          <div className="text-center py-4">
            <p className="text-gray-600">Confirmer que le bain a été donné à l'heure indiquée.</p>
          </div>
        )}

        {/* Boutons */}
        <div className="flex space-x-3 pt-4">
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 btn-secondary"
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={loading}
            className="flex-1 btn-primary disabled:opacity-50"
          >
            {loading ? (event ? 'Modification...' : 'Enregistrement...') : (event ? 'Modifier' : 'Enregistrer')}
          </button>
        </div>
      </form>
    </div>
  );
}

export default EventForm;
